/**
 * ChatNotificationContext — Student Side
 *
 * Tracks per-order unread message counts in real-time via socket.
 * Listens for 'new-notification' events from the backend and increments
 * the count for that orderId unless the student is already in that chat.
 *
 * Exposes:
 *   getUnreadCount(orderId)  — returns the unread count for a specific order
 *   markOrderRead(orderId)   — resets unread count (called on join-order)
 *   setActiveOrderId(id)     — suppresses notifications for the open chat
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { getSocket, initSocket } from '../utils/socket';
import { playNotificationSound, startTabAlert, stopTabAlert } from '../utils/notificationUtils';

const ChatNotificationContext = createContext(null);

export const useChatNotification = () => {
  const ctx = useContext(ChatNotificationContext);
  if (!ctx) throw new Error('useChatNotification must be used inside ChatNotificationProvider');
  return ctx;
};

export const ChatNotificationProvider = ({ children }) => {
  // { [orderId]: number }
  const [unreadCounts, setUnreadCounts] = useState({});

  // Ref to the orderId whose chat panel is currently open (suppress notifications for it)
  const activeOrderIdRef = useRef(null);

  const setActiveOrderId = useCallback((orderId) => {
    activeOrderIdRef.current = orderId;
  }, []);

  const getUnreadCount = useCallback(
    (orderId) => unreadCounts[orderId] || 0,
    [unreadCounts]
  );

  const markOrderRead = useCallback((orderId) => {
    setUnreadCounts((prev) => {
      if (!prev[orderId]) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  }, []);

  // Listen to incoming notifications from the backend
  // Use an interval to keep checking for the socket until it's available,
  // because initSocket() is called later by ChatFAB when a user visits an order page.
  useEffect(() => {
    let cleanupFn = null;
    let intervalId = null;
    let attached = false;

    const handleNotification = (data) => {
      const { orderId } = data;
      if (!orderId) return;

      // Suppress if student has this chat open and joined the room
      if (activeOrderIdRef.current && activeOrderIdRef.current === orderId) {
        return;
      }

      setUnreadCounts((prev) => ({
        ...prev,
        [orderId]: (prev[orderId] || 0) + 1,
      }));

      // Play sound and flash tab title
      playNotificationSound();
      if (document.visibilityState !== 'visible') {
        startTabAlert('(1) New Message - DoubtQ');
      }
    };

    const tryAttach = () => {
      if (attached) return;

      // Try to get existing socket, or initialize one if logged in
      let socket = getSocket();
      if (!socket) {
        const token = localStorage.getItem('token');
        if (token) {
          socket = initSocket(token);
        }
      }

      if (!socket) return; // Not logged in yet, keep polling

      socket.on('new-notification', handleNotification);
      attached = true;

      // Also re-attach on reconnect (socket.io may drop and reconnect)
      socket.on('connect', () => {
        console.log('[ChatNotification] Socket reconnected, listener still active');
      });

      // Stop polling once attached
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Try immediately
    tryAttach();

    // If not attached yet, poll every 3 seconds until socket is available
    if (!attached) {
      intervalId = setInterval(tryAttach, 3000);
    }

    const handleFocus = () => stopTabAlert();
    window.addEventListener('focus', handleFocus);

    return () => {
      if (intervalId) clearInterval(intervalId);
      const socket = getSocket();
      if (socket) {
        socket.off('new-notification', handleNotification);
      }
      window.removeEventListener('focus', handleFocus);
      stopTabAlert();
    };
  }, []);

  return (
    <ChatNotificationContext.Provider
      value={{ getUnreadCount, markOrderRead, setActiveOrderId, unreadCounts }}
    >
      {children}
    </ChatNotificationContext.Provider>
  );
};

export default ChatNotificationContext;
